/**
 * Shared License Management dummy data
 *
 * Single source of truth for the *relationships* between an identity and its
 * accounts, and between an account and its roles. All names are unique and
 * consistently formatted, and are derived deterministically from stable inputs
 * (identityId / accountKey) so the same identity always yields the same
 * accounts and roles across every page:
 *   - FUE Inventory table rows              (mockFueInventoryData.ts)
 *   - FUE Inventory side panel (accounts)   (searchIdentityAccounts)
 *   - FUE Inventory side panel (role tree)  (searchAccountRoles)
 *   - Overview review list                  (generateMockReviewItems)
 *
 * The generators are pure functions of their inputs — no randomness — so the
 * data is reproducible and stable between renders.
 */

import type {
  AccountRole,
  AccountRoleChildRole,
  IdentityAccountRow,
} from '../data-layer/licenseManagementApi';

/** SAP endpoint/system names used for account grouping. */
export const SAP_ENDPOINTS = [
  { key: 701, name: 'SAP ECC Production' },
  { key: 702, name: 'SAP S/4HANA' },
  { key: 703, name: 'SAP BW/4HANA' },
  { key: 704, name: 'SAP SuccessFactors' },
  { key: 705, name: 'SAP Ariba' },
] as const;

export const TIERS = [
  'Advanced User',
  'Core User',
  'Self-Service User',
] as const;

const ACCOUNT_STATUSES = ['ACTIVE', 'INACTIVE', 'LOCKED'] as const;

/**
 * Business-domain role name stems. Combined with an endpoint code + running
 * index they produce unique, SAP-style role names such as
 * `Z_ECC_FI_GL_MANAGER_01`.
 */
const ROLE_DOMAINS = [
  { area: 'FI', role: 'GL_MANAGER' },
  { area: 'FI', role: 'AP_CLERK' },
  { area: 'CO', role: 'COST_CENTER_MGR' },
  { area: 'MM', role: 'PURCHASER' },
  { area: 'SD', role: 'SALES_REP' },
  { area: 'HR', role: 'PAYROLL_ADMIN' },
  { area: 'PP', role: 'PRODUCTION_PLANNER' },
  { area: 'WM', role: 'WAREHOUSE_CLERK' },
  { area: 'BASIS', role: 'SYS_ADMIN' },
  { area: 'SEC', role: 'AUTH_ADMIN' },
] as const;

/** Composite role bundle names, keyed by SAP module. */
const COMPOSITE_DOMAINS = [
  { area: 'FI', role: 'FINANCE_POWER_USER' },
  { area: 'MM', role: 'PROCUREMENT_LEAD' },
  { area: 'SD', role: 'SALES_MANAGER' },
  { area: 'HR', role: 'HR_BUSINESS_PARTNER' },
] as const;

/** Short endpoint code used as a prefix so role names stay unique per system. */
function endpointCode(endpointKey: number): string {
  const map: Record<number, string> = {
    701: 'ECC',
    702: 'S4H',
    703: 'BW4',
    704: 'SF',
    705: 'ARB',
  };

  return map[endpointKey] ?? 'SAP';
}

/** A small, stable hash so different identity ids fan out to different data. */
function stableHash(input: string): number {
  let hash = 0;

  for (let i = 0; i < input.length; i += 1) {
    // Keep the running value bounded without bitwise ops.
    hash = (hash * 31 + input.charCodeAt(i)) % 2147483647;
  }

  return hash;
}

/**
 * Deterministic account list for a given identity.
 * Every identity gets 2–4 accounts across distinct SAP endpoints, so account
 * names are unique within the identity and stable across renders.
 */
export function getAccountsForIdentity(
  identityId: string
): IdentityAccountRow[] {
  const seed = stableHash(identityId);
  const count = 2 + (seed % 3); // 2–4 accounts

  return Array.from({ length: count }, (_unused, i) => {
    const endpoint = SAP_ENDPOINTS[(seed + i) % SAP_ENDPOINTS.length];
    const tier = TIERS[(seed + i) % TIERS.length];
    // Unique, human-readable account name, e.g. "ECC-ALICE-JOHNSON".
    const normalizedId = identityId.toUpperCase().replace(/[^A-Z0-9]+/g, '-');

    return {
      accountKey: seed * 10 + i,
      accountName: `${endpointCode(endpoint.key)}-${normalizedId}`,
      endpointKey: endpoint.key,
      endpointName: endpoint.name,
      highestTierWeight: (i % 3) + 1,
      currentTier: tier,
      status: ACCOUNT_STATUSES[(seed + i) % ACCOUNT_STATUSES.length],
    };
  });
}

/**
 * Deterministic role list for a given account.
 * Produces a mix of composite roles (with unique child roles) and single roles.
 * Role names are unique per account via the account-scoped running index.
 */
export function getRolesForAccount(accountKey: number): AccountRole[] {
  const seed = accountKey;
  const code = endpointCode(701 + (seed % SAP_ENDPOINTS.length));
  const total = 5 + (seed % 4); // 5–8 roles per account

  return Array.from({ length: total }, (_unused, i) => {
    const isComposite = i % 3 === 0;

    if (isComposite) {
      const domain = COMPOSITE_DOMAINS[(seed + i) % COMPOSITE_DOMAINS.length];
      const childCount = 2 + ((seed + i) % 3); // 2–4 child roles
      const childRoles: AccountRoleChildRole[] = Array.from(
        { length: childCount },
        (_c, c) => {
          const childDomain =
            ROLE_DOMAINS[(seed + i + c) % ROLE_DOMAINS.length];

          return {
            roleKey: seed * 1000 + i * 10 + c + 1,
            roleName: `Z_${code}_${childDomain.area}_${
              childDomain.role
            }_${String(c + 1).padStart(2, '0')}`,
            tierWeight: (c % 3) + 1,
          };
        }
      );

      return {
        roleKey: seed * 1000 + i * 10,
        roleName: `Z_${code}_${domain.area}_${domain.role}_${String(
          i + 1
        ).padStart(2, '0')}`,
        roleType: 'Composite',
        tierWeight: ((seed + i) % 4) + 1,
        entitlementUsed: (seed + i) % 2 === 0,
        childRoles,
      };
    }

    const domain = ROLE_DOMAINS[(seed + i) % ROLE_DOMAINS.length];

    return {
      roleKey: seed * 1000 + i * 10,
      roleName: `Z_${code}_${domain.area}_${domain.role}_${String(
        i + 1
      ).padStart(2, '0')}`,
      roleType: 'Single',
      tierWeight: ((seed + i) % 4) + 1,
      entitlementUsed: (seed + i) % 2 === 0,
      childRoles: [],
    };
  });
}

/**
 * Returns a direct list of roles for an identity (simulates a dedicated
 * identity-roles API endpoint, independent of account-level role lookups).
 */
export function getRolesForIdentity(identityId: string): AccountRole[] {
  const seed = stableHash(identityId);
  const total = 6 + (seed % 5); // 6–10 roles per identity

  return Array.from({ length: total }, (_unused, i) => {
    const isComposite = i % 3 === 0;
    const roleKey = seed * 500 + i * 10;

    if (isComposite) {
      const domain = COMPOSITE_DOMAINS[(seed + i) % COMPOSITE_DOMAINS.length];
      const childCount = 2 + ((seed + i) % 3);
      const childRoles: AccountRoleChildRole[] = Array.from(
        { length: childCount },
        (_c, c) => {
          const childDomain =
            ROLE_DOMAINS[(seed + i + c) % ROLE_DOMAINS.length];

          return {
            roleKey: roleKey + c + 1,
            roleName: `ROLE_${childDomain.area}_${childDomain.role}_${String(
              c + 1
            ).padStart(2, '0')}`,
            tierWeight: (c % 3) + 1,
          };
        }
      );

      return {
        roleKey,
        roleName: `ROLE_${domain.area}_${domain.role}_${String(i + 1).padStart(
          2,
          '0'
        )}`,
        roleType: 'Composite',
        tierWeight: ((seed + i) % 4) + 1,
        entitlementUsed: (seed + i) % 2 === 0,
        childRoles,
      };
    }

    const domain = ROLE_DOMAINS[(seed + i) % ROLE_DOMAINS.length];

    return {
      roleKey,
      roleName: `ROLE_${domain.area}_${domain.role}_${String(i + 1).padStart(
        2,
        '0'
      )}`,
      roleType: 'Single',
      tierWeight: ((seed + i) % 4) + 1,
      entitlementUsed: (seed + i) % 2 === 0,
      childRoles: [],
    };
  });
}

/**
 * Canonical set of "Human" identities used by the Overview review list so the
 * names shown there are consistent with the FUE Inventory Human rows.
 * These mirror the first entries produced in mockFueInventoryData.ts.
 */
export interface SharedIdentity {
  identityId: string;
  userId: string;
  fullName: string;
  department: string;
  identityType: string;
}

const HUMAN_SEED = [
  { user: 'alice.johnson', name: 'Alice Johnson', dept: 'Engineering' },
  { user: 'bob.smith', name: 'Bob Smith', dept: 'Finance' },
  { user: 'carol.white', name: 'Carol White', dept: 'HR' },
  { user: 'david.brown', name: 'David Brown', dept: 'Sales' },
  { user: 'eve.davis', name: 'Eve Davis', dept: 'Marketing' },
  { user: 'frank.miller', name: 'Frank Miller', dept: 'Legal' },
];

/** Shared human identities — the single source for the review list names. */
export const SHARED_HUMAN_IDENTITIES: SharedIdentity[] = HUMAN_SEED.map(
  (h, i) => ({
    identityId: `human-${i + 1}`,
    userId: h.user,
    fullName: h.name,
    department: h.dept,
    identityType: 'Human',
  })
);
